// 치료 이력 타임라인 — 레이아웃 계산.
//   React 와 무관한 순수 함수다. 입력은 /api/getTreatmentTimeline 응답,
//   출력은 SVG 로 그릴 좌표 묶음. 글자 폭은 measure(text, size, weight) 로 실제로 잰다.
//
//   규칙 (연구원 시안 기준)
//   · 가로축 0 = 가장 이른 날짜, 1개월 = 30.44일
//   · 한 줄 막대. 병용 구간은 약물 수만큼 가로로 등분해 위에서부터 칠한다
//     (겹치는 치료는 구간을 잘라 약물을 합친다 — Erlotinib 진행 중 C/P 추가 → 3제 구간)
//   · 치료 사이 0.5개월 이상 빈 곳은 빗금 (축 끝 빈칸은 제외 — 끝을 모르는 것이지 무치료가 아니다)
//   · 종료일 없는 단일 날짜 = 1회 투여(▼)
//   · 종료일이 없는 범위("~ )" · "~ ongoing") = 종료일 미기재. "진행중"이라고 단정하지 않는다
//     (채취 후 추적을 계속하지 않아 실제로 끝났는지 모른다 — PDO평가팀 2026-10-01)
//     막대는 다음 약물 치료 시작 전날까지, 마지막 치료면 1개월만 그리고 끝을 흐린다.
//     범례는 "시작일 ~" 만 쓰고 기간 칸은 비운다
//     근거: 뒤에 다른 치료가 오는 61건은 대부분 1차→2차 같은 순차 요법이고 19건은 원문에 PD 가 있다
//   · 날짜가 "YYYY-MM" 까지만 적힌 종료일은 그 달 말일로 본다 (regimen_raw 로 판정)

import { drugStyle, EVENT_COLORS, UNKNOWN_FILL } from './drugPalette';

const DAY = 86400000;
const MONTH = 30.44 * DAY;
const GAP_MIN_MONTHS = 0.5;

export const FONT = "'Noto Sans KR','Malgun Gothic','Apple SD Gothic Neo','NanumGothic',sans-serif";

// 시안 비율 — 가로 960 고정, 세로는 내용에 맞춘다
const W = 960;
const PL = 28;
const PR = 28;
const PW = W - PL - PR;
const BAR_H = 46;

function parseYmd(s) {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
}

function endOfMonth(ms) {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
}

function fmt(ms, monthOnly) {
    const d = new Date(ms);
    const y = d.getUTCFullYear();
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
    if (monthOnly) return `${y}.${mo}`;
    return `${y}.${mo}.${String(d.getUTCDate()).padStart(2, '0')}`;
}

// regimen_raw 에서 날짜 정밀도를 읽는다. DB 의 start_date/end_date 는 월만 적힌 값도 1일로 들어가 있다.
function rawPrecision(raw) {
    const s = String(raw || '');
    const m = s.match(/(\d{4}-\d{2}(?:-\d{2})?)\s*~\s*(\d{4}-\d{2}(?:-\d{2})?)?/);
    if (m) return { range: true, startMonth: m[1].length === 7, endMonth: !!m[2] && m[2].length === 7 };
    const one = s.match(/\d{4}-\d{2}(?:-\d{2})?/);
    return { range: false, startMonth: !!one && one[0].length === 7, endMonth: false };
}

// "RT for lung (2019-10-30 ~ 2019-11-19)" → "RT for lung"
function stripDates(raw) {
    return String(raw || '').replace(/\s*\([^)]*\d{4}-\d{2}[^)]*\)/g, '').replace(/\s*:.*$/, '').trim();
}

function normalize(t) {
    const kind = t.type === 'radiation' ? 'radiation' : t.type === 'procedure' ? 'procedure' : 'drug';
    const prec = rawPrecision(t.regimenRaw);
    let start = parseYmd(t.start);
    let end = parseYmd(t.end);
    if (end != null && prec.endMonth) end = endOfMonth(end);
    if (start != null && end != null && end < start) end = start;
    const open = start != null && end == null && (!!t.ongoing || prec.range);
    const drugs = (t.drugs && t.drugs.length) ? t.drugs : [t.regimenName || stripDates(t.regimenRaw) || 'Unknown'];
    return {
        id: t.id,
        kind,
        drugs,
        name: kind === 'drug' ? drugs.join(' + ') : (stripDates(t.regimenRaw) || t.regimenName),
        start, end, open,
        startMonth: prec.startMonth,
        endMonth: prec.endMonth,
        single: start != null && end == null && !open,
    };
}

function isNaive(t) {
    return /^na[iï]ve/i.test(String(t.regimenRaw || '').trim());
}

/**
 * @param data     { organoid: {key, labCode, surgeryDate, sampleSource}, treatments: [...] }
 * @param opts     { showRadiation, measure, t, idPrefix }
 */
export function buildLayout(data, opts) {
    const { measure, t } = opts;
    const org = data.organoid || {};
    // 제목은 오가노이드 코드만. LabCode(YUO-014 등 수집기관 원 코드)는 넣지 않는다 — 내려받은 이미지가 밖으로 돌 수 있다
    const title = String(org.key || '').replace('hashmark', '#');

    const raw = data.treatments || [];
    const naive = raw.length > 0 && raw.every(isNaive);
    const items = raw.filter((x) => !isNaive(x)).map(normalize);

    // 종료일 미기재 — 그릴 끝을 정한다 (표시용. DB 는 그대로)
    const drugStarts = items.filter((i) => i.kind === 'drug' && i.start != null).map((i) => i.start).sort((a, b) => a - b);
    items.forEach((i) => {
        if (i.kind !== 'drug' || !i.open) return;
        const next = drugStarts.find((s) => s > i.start);
        i.end = next != null ? Math.max(i.start, next - DAY) : i.start + MONTH - DAY;
    });

    const drugBars = items.filter((i) => i.kind === 'drug' && i.start != null && i.end != null);
    const drugPoints = items.filter((i) => i.kind === 'drug' && i.single);
    const procs = items.filter((i) => i.kind !== 'drug' && i.start != null);
    const undated = items.filter((i) => i.start == null);

    // 이벤트 — 채취 시점은 항상, 방사선·수술은 선택
    const events = [];
    const sampled = parseYmd(org.surgeryDate);
    if (sampled != null) {
        events.push({ kind: 'sampling', date: sampled, label: org.sampleSource || t('채취'), dateText: fmt(sampled) });
    }
    if (opts.showRadiation) {
        procs.forEach((p) => events.push({
            kind: p.kind, date: p.start, end: p.end, label: p.name,
            dateText: p.end != null && p.end !== p.start
                ? `${fmt(p.start, p.startMonth)} ~ ${fmt(p.end, p.endMonth)}` : fmt(p.start, p.startMonth),
        }));
    }
    events.sort((a, b) => a.date - b.date);

    const base = { width: W, title, naive };

    const dated = [];
    drugBars.forEach((b) => { dated.push(b.start); if (b.end != null) dated.push(b.end); });
    drugPoints.forEach((p) => dated.push(p.start));
    events.forEach((e) => { dated.push(e.date); if (e.end != null) dated.push(e.end); });

    // 그릴 치료가 없으면(채취일만 있는 경우 포함) 막대 없이 안내만 한다
    const drawable = drugBars.length + drugPoints.length + (opts.showRadiation ? procs.length : 0);
    if (!drawable) {
        const message = naive ? t('치료 이력 없음 (Naïve)')
            : items.length ? t('날짜가 기재된 치료 이력이 없습니다') : t('치료 이력이 없습니다');
        const legendTop = 72;
        const legend = legendRows([], [], undated, opts, legendTop);
        return { ...base, message, legend, height: legendTop + legend.length * 26 + (legend.length ? 16 : 0) };
    }

    /* ── 가로축 ───────────────────────────────────────── */
    const origin = Math.min(...dated);
    const lastKnown = Math.max(...dated);
    const total = Math.max(1, Math.ceil((lastKnown - origin) / MONTH - 1e-6));
    const axisRight = origin + total * MONTH;
    const xOf = (ms) => PL + ((ms - origin) / MONTH) / total * PW;

    /* ── 세로 배치 ─────────────────────────────────────── */
    let y = 44;
    const markerRow = drugPoints.length > 0;
    if (markerRow) y += 16;
    const bar = { x: PL, y, w: PW, h: BAR_H };
    const barBottom = y + BAR_H;

    /* ── 막대 구간 ─────────────────────────────────────── */
    const ivs = drugBars.map((b) => ({ item: b, s: b.start, e: Math.min(axisRight, b.end + DAY) }));
    // 축 끝 빈칸은 그 안에 1회 투여·이벤트가 있을 때만 빗금 (없으면 올림으로 생긴 여백일 뿐)
    const laterMarks = [...drugPoints.map((p) => p.start), ...events.map((e) => e.date)];
    const cuts = new Set([origin, axisRight]);
    ivs.forEach((iv) => { cuts.add(Math.max(origin, iv.s)); cuts.add(Math.min(axisRight, iv.e)); });
    const bounds = [...cuts].sort((a, b) => a - b);

    const pieces = [];
    for (let k = 0; k < bounds.length - 1; k++) {
        const a = bounds[k], b = bounds[k + 1];
        if (b <= a) continue;
        const active = ivs.filter((iv) => iv.s <= a && iv.e >= b)
            .sort((p, q) => p.item.start - q.item.start || p.item.id - q.item.id);
        const key = active.map((iv) => iv.item.id).join(',');
        const last = pieces[pieces.length - 1];
        if (last && last.key === key) { last.b = b; continue; }
        pieces.push({ a, b, key, active });
    }

    const segments = pieces.map((p) => {
        const x = xOf(p.a);
        const w = xOf(p.b) - x;
        if (!p.active.length) {
            const trailing = p.b >= axisRight && !laterMarks.some((d) => d > p.a);
            return { x, w, gap: !trailing && (p.b - p.a) / MONTH >= GAP_MIN_MONTHS, rows: [] };
        }
        const names = [];
        p.active.forEach((iv) => iv.item.drugs.forEach((d) => { if (names.indexOf(d) === -1) names.push(d); }));
        const n = names.length;
        const rowH = BAR_H / n;
        const size = n === 1 ? 12 : n === 2 ? 10.5 : n === 3 ? 9 : 8;
        const rows = names.map((name) => {
            const st = drugStyle(name);
            // 전체 이름 → 약어 → 작은 약어 순으로 들어가는 것을 쓴다
            let label = null;
            let fs = size;
            if (rowH >= size + 2) {
                for (const [cand, sz] of [[name, size], [st.abbr, size], [st.abbr, Math.max(7.5, size - 2)]]) {
                    if (measure(cand, sz, 500) + 6 <= w) { label = cand; fs = sz; break; }
                }
            }
            return { fill: st.fill, text: st.text, label, size: fs };
        });
        // 종료일 미기재 치료가 끝나는 조각은 끝을 흐린다
        const fade = p.active.some((iv) => iv.item.open && iv.e <= p.b);
        const fadeW = fade ? Math.min(90, w * 0.6) : 0;   // 짧은 막대도 앞쪽 색은 남긴다
        return { x, w, rows, fade, fadeW };
    });

    /* ── 1회 투여 마커 ─────────────────────────────────── */
    const markers = drugPoints.map((p) => {
        const st = drugStyle(p.drugs[0]);
        return { x: xOf(p.start), y: bar.y - 14, fill: st.fill, stroke: st.text };
    });

    /* ── 눈금자 ───────────────────────────────────────── */
    const major = total <= 26 ? 3 : total <= 60 ? 6 : total <= 144 ? 12 : 24;
    const minor = total <= 60 ? 1 : total <= 144 ? 3 : 6;
    const ticks = [];
    for (let m = 0; m <= total; m += minor) {
        const isMajor = m % major === 0 || m === total;
        let label = null;
        if (m === total) label = monthLabel(total, t);
        else if (m % major === 0 && (m === 0 || total - m >= major / 2)) label = String(m);
        ticks.push({ x: PL + (m / total) * PW, major: isMajor, label, last: m === total });
    }
    if (total % minor !== 0) ticks.push({ x: PL + PW, major: true, label: monthLabel(total, t), last: true });

    const rulerY = barBottom + 4;
    const dateY = rulerY + 34;
    const years = [];
    const d0 = new Date(origin);
    for (let yr = d0.getUTCFullYear() + 1; ; yr++) {
        const ms = Date.UTC(yr, 0, 1);
        if (ms >= axisRight) break;
        const x = xOf(ms);
        const rel = (x - PL) / PW;
        years.push({ x, label: rel < 0.13 || rel > 0.97 ? null : String(yr) });
    }
    const startLabel = fmt(origin, true);

    /* ── 이벤트 레인 ───────────────────────────────────── */
    const sepY = dateY + 12;
    const laneTop = sepY + 22;
    const rowsUsed = [];
    const eventTicks = [];
    const eventLabels = events.map((ev) => {
        const x = xOf(ev.date);
        const color = EVENT_COLORS[ev.kind] || EVENT_COLORS.sampling;
        eventTicks.push({ x, color });
        if (ev.end != null && ev.end !== ev.date) eventTicks.push({ x: xOf(ev.end), color, faint: true });
        const text = `▲ ${ev.label} ${ev.dateText}`;
        const w = measure(text, 12, 500);
        const right = (x - PL) / PW > 0.6;
        const x0 = right ? x + 5 - w : x - 5;
        const x1 = x0 + w;
        let row = 0;
        while ((rowsUsed[row] || []).some(([p, q]) => x0 < q + 10 && x1 > p - 10)) row++;
        (rowsUsed[row] = rowsUsed[row] || []).push([x0, x1]);
        return { x: right ? x + 5 : x - 5, y: laneTop + row * 20, anchor: right ? 'end' : 'start', color, label: ev.label, dateText: ev.dateText, right };
    });
    const laneBottom = events.length ? laneTop + (rowsUsed.length - 1) * 20 + 14 : sepY;

    /* ── 범례 ─────────────────────────────────────────── */
    const legendTop = laneBottom + 26;
    const legend = legendRows([...drugBars, ...drugPoints], opts.showRadiation ? procs : [], undated, opts, legendTop);
    const height = legendTop + legend.length * 26 + 14;

    return {
        ...base, height, total, bar, segments, markers, ticks, rulerY, dateY, startLabel, years,
        sepY, eventTicks, eventLabels, legend,
    };
}

// "N개월" — 영어는 1 이면 단수(1 month), 그 외 복수(9 months)
function monthLabel(n, t) {
    return n === 1 ? t('1개월') : t('{{n}}개월', { n });
}

function monthsText(ms, t) {
    const m = ms / MONTH;
    if (m < 0.5) return t('1개월 미만');
    return monthLabel(Math.round(m), t);
}

function legendRows(drugItems, procItems, undated, opts, top) {
    const { t, measure } = opts;
    const rows = [];
    const byStart = (a, b) => a.start - b.start || a.id - b.id;

    drugItems.slice().sort(byStart).forEach((i) => {
        let dates, months, chip;
        const s = fmt(i.start, i.startMonth);
        if (i.single) {
            dates = s; months = t('1회'); chip = 'single';
        } else if (i.open) {
            dates = `${s} ~`; months = ''; chip = 'fade';
        } else {
            dates = `${s} ~ ${fmt(i.end, i.endMonth)}`; months = monthsText(i.end + DAY - i.start, t); chip = 'bar';
        }
        rows.push({
            chip, fills: i.drugs.map((d) => drugStyle(d).fill), stroke: drugStyle(i.drugs[0]).text,
            name: i.name, combo: i.drugs.length > 1, dates, months,
        });
    });

    procItems.slice().sort(byStart).forEach((i) => {
        const s = fmt(i.start, i.startMonth);
        rows.push({
            chip: 'event', fills: [EVENT_COLORS[i.kind]], name: i.name,
            badge: i.kind === 'radiation' ? t('방사선') : t('수술'),
            dates: i.end != null && i.end !== i.start ? `${s} ~ ${fmt(i.end, i.endMonth)}` : s,
            months: i.end != null && i.end !== i.start ? monthsText(i.end + DAY - i.start, t) : t('1회'),
        });
    });

    undated.forEach((i) => rows.push({
        chip: 'none', fills: [UNKNOWN_FILL], name: i.name, combo: i.kind === 'drug' && i.drugs.length > 1,
        dates: t('날짜 미기재'), months: '', muted: true,
    }));

    // 이름이 날짜 칸을 넘으면 말줄임
    const nameMax = 440;
    return rows.map((r, idx) => {
        let name = r.name;
        const extra = (r.combo || r.badge) ? 44 : 0;
        if (measure(name, 13, 500) + extra > nameMax) {
            while (name.length > 4 && measure(name + '…', 13, 500) + extra > nameMax) name = name.slice(0, -1);
            name += '…';
        }
        return { ...r, name, nameWidth: measure(name, 13, 500), y: top + idx * 26 };
    });
}

export const LAYOUT = { W, PL, PR, PW, BAR_H };
